import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {SVC_ICONS, hubLang} from '../../lib/curo-services.js';

/**
 * 쉬는 순간 제안 — 손이 멈춘 틈에만 다른 큐브로이드 서비스를 슬쩍 권한다.
 * 2026-07-24. 대표: "Studio가 방문자 1등인데 시뮬레이터 등 다른 사이트로 더 잘 가는 방법?"
 *
 * ⚠️ 잘못 만들면 광고 팝업이 된다. 그래서 조건을 엄격하게:
 *   ① **작업 중엔 절대 안 뜸** — 마우스/키보드/블록변경이 3분간 전혀 없을 때만(진짜 쉬는 순간)
 *   ② **하루 1회** — 오늘 이미 봤으면(또는 닫았으면) 끝
 *   ③ **누적 3회 무시하면 영구 중단** — 관심 없는 사람에게 계속 들이대지 않는다
 *   ④ **처음 5분 안엔 안 뜸** — 막 들어온 사람을 내보내지 않는다
 *   ⑤ 연령대가 Studio와 겹치는 서비스만(초3-4~중고등). 유아용을 중학생에게 권하지 않는다
 *   ⑥ 작은 카드 + 언제든 닫기. 화면을 가리지 않는다(pointer-events 는 카드에만)
 */

const SEEN_DAY = 'curoai_suggest_day';   // 마지막으로 보여준 날짜
const IGNORED = 'curoai_suggest_ignore'; // 무시 누적 횟수
// 교실 기준: 쉬는시간 10분. 3분은 "잠깐 생각 중"일 수도 있어 너무 이르다 → 5분(확실히 손 뗀 상태).
const IDLE_MS = 300000;  // 5분 무동작 = 쉬는 중
const GRACE_MS = 300000; // 접속 후 5분은 건드리지 않음(막 들어온 사람 안 내보냄)
const MAX_IGNORE = 3;

// Studio 사용자(초3-4~중고등)와 겹치는 것만. key = SVC_ICONS 의 아이콘 키.
const PICKS = [
    {icon: 'simulator', url: 'https://simulator.cubroid.com/', color: '#8E63E6', t: 'sim'},
    {icon: 'ml', url: 'https://ml.cubroid.com/', color: '#8E63E6', t: 'ml'},
    {icon: 'weather', url: 'https://weather.cubroid.com/', color: '#14B0A6', t: 'weather'},
    {icon: 'imitation', url: 'https://imitation.cubroid.com/', color: '#FF6B5A', t: 'motion'},
    {icon: 'agent', url: 'https://agent.cubroid.com/', color: '#C84BEB', t: 'agent'}
];

const L = {
    en: {
        head: 'Taking a break?', go: 'Try it', no: 'Not now',
        sim: 'Build and drive a robot in 3D — no hardware needed',
        ml: 'Train an image-recognition model and get a report',
        weather: 'Pull real weather data and turn it into a chart',
        motion: 'Record a robot motion and play it back',
        agent: 'Let GPT-4 Vision see through the camera and act'
    },
    ko: {
        head: '잠깐 쉬는 중인가요?', go: '해보기', no: '나중에',
        sim: '3D로 로봇을 조립하고 움직여봐요 · 실물 없어도 돼요',
        ml: '이미지 분류 모델을 학습시키고 보고서까지 만들어요',
        weather: '실시간 날씨 데이터를 가져와 차트로 그려요',
        motion: '로봇 동작을 기록하고 그대로 재현해요',
        agent: 'GPT-4 Vision이 카메라로 보고 스스로 움직여요'
    },
    vi: {head: 'Đang nghỉ một chút?', go: 'Thử ngay', no: 'Để sau',
        sim: 'Lắp và điều khiển robot 3D — không cần phần cứng',
        ml: 'Huấn luyện mô hình nhận diện ảnh và tạo báo cáo',
        weather: 'Lấy dữ liệu thời tiết thật và vẽ biểu đồ',
        motion: 'Ghi lại chuyển động robot và phát lại',
        agent: 'GPT-4 Vision nhìn qua camera và tự hành động'},
    ja: {head: 'ひとやすみ中？', go: 'やってみる', no: 'あとで',
        sim: '3Dでロボットを組み立てて動かそう · 実物がなくてもOK',
        ml: '画像分類モデルを学習させてレポートまで作ろう',
        weather: 'リアルタイムのお天気データをグラフにしよう',
        motion: 'ロボットの動きを記録して再生しよう',
        agent: 'GPT-4 Visionがカメラで見て自分で動くよ'},
    'zh-cn': {head: '在休息吗？', go: '试试看', no: '以后再说',
        sim: '在 3D 里组装并驾驶机器人 · 不需要实物',
        ml: '训练图像分类模型并生成报告',
        weather: '获取实时天气数据并绘制图表',
        motion: '录制机器人动作并回放',
        agent: '让 GPT-4 Vision 通过摄像头观察并自主行动'},
    'zh-tw': {head: '在休息嗎？', go: '試試看', no: '以後再說',
        sim: '在 3D 裡組裝並駕駛機器人 · 不需要實物',
        ml: '訓練影像分類模型並產生報告',
        weather: '取得即時天氣資料並繪製圖表',
        motion: '錄製機器人動作並回放',
        agent: '讓 GPT-4 Vision 透過攝影機觀察並自主行動'},
    de: {head: 'Kurze Pause?', go: 'Ausprobieren', no: 'Später',
        sim: 'Baue und fahre einen Roboter in 3D — ganz ohne Hardware',
        ml: 'Trainiere ein Bilderkennungsmodell und erhalte einen Bericht',
        weather: 'Hole echte Wetterdaten und mach ein Diagramm daraus',
        motion: 'Nimm eine Roboterbewegung auf und spiel sie ab',
        agent: 'GPT-4 Vision schaut durch die Kamera und handelt selbst'},
    es: {head: '¿Un descanso?', go: 'Probar', no: 'Luego',
        sim: 'Monta y conduce un robot en 3D — sin hardware',
        ml: 'Entrena un modelo de reconocimiento de imágenes y obtén un informe',
        weather: 'Trae datos del tiempo reales y haz un gráfico',
        motion: 'Graba un movimiento del robot y reprodúcelo',
        agent: 'Deja que GPT-4 Vision vea por la cámara y actúe'},
    fr: {head: 'Une petite pause ?', go: 'Essayer', no: 'Plus tard',
        sim: 'Construis et pilote un robot en 3D — sans matériel',
        ml: "Entraîne un modèle de reconnaissance d'images et obtiens un rapport",
        weather: 'Récupère la météo réelle et transforme-la en graphique',
        motion: 'Enregistre un mouvement de robot et rejoue-le',
        agent: 'GPT-4 Vision regarde par la caméra et agit tout seul'},
    it: {head: 'Una pausa?', go: 'Prova', no: 'Più tardi',
        sim: 'Costruisci e guida un robot in 3D — senza hardware',
        ml: 'Addestra un modello di riconoscimento immagini e ottieni un report',
        weather: 'Prendi dati meteo reali e trasformali in un grafico',
        motion: 'Registra un movimento del robot e riproducilo',
        agent: 'GPT-4 Vision guarda dalla fotocamera e agisce da solo'},
    ru: {head: 'Небольшой перерыв?', go: 'Попробовать', no: 'Позже',
        sim: 'Собери и води робота в 3D — без реального железа',
        ml: 'Обучи модель распознавания изображений и получи отчёт',
        weather: 'Возьми реальные данные о погоде и построй график',
        motion: 'Запиши движение робота и воспроизведи его',
        agent: 'GPT-4 Vision смотрит через камеру и действует сам'}
};

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

class IdleSuggest extends React.Component {
    constructor (props) {
        super(props);
        this.state = {pick: null};
        this._bump = this._bump.bind(this);
        this._tick = this._tick.bind(this);
        this._last = Date.now();
        this._start = Date.now();
    }
    componentDidMount () {
        // 테스트용: 콘솔에서 curoSuggestNow() 하면 조건 무시하고 바로 띄운다(대기 없이 확인).
        // 실사용에는 영향 없음 — 아무도 호출하지 않으면 그냥 존재만 하는 함수.
        try {
            window.curoSuggestNow = () => {
                const d = new Date();
                const idx = (d.getFullYear() + d.getMonth() + d.getDate()) % PICKS.length;
                this.setState({pick: PICKS[idx]});
            };
        } catch (e) { /* noop */ }
        // 이미 오늘 봤거나, 3번 무시했으면 아예 시작도 안 함
        if (this._doneForNow()) return;
        ['mousedown', 'keydown', 'wheel', 'touchstart'].forEach(e =>
            window.addEventListener(e, this._bump, {passive: true}));
        try { this.props.vm.on('PROJECT_CHANGED', this._bump); } catch (e) { /* noop */ }
        this._timer = setInterval(this._tick, 20000);
    }
    componentWillUnmount () {
        ['mousedown', 'keydown', 'wheel', 'touchstart'].forEach(e =>
            window.removeEventListener(e, this._bump));
        try { this.props.vm.off('PROJECT_CHANGED', this._bump); } catch (e) { /* noop */ }
        clearInterval(this._timer);
    }
    _doneForNow () {
        try {
            if (localStorage.getItem(SEEN_DAY) === today()) return true;
            if (parseInt(localStorage.getItem(IGNORED) || '0', 10) >= MAX_IGNORE) return true;
        } catch (e) { return true; } // localStorage 못 쓰면 아예 안 띄움(안전 쪽)
        return false;
    }
    _bump () { this._last = Date.now(); }
    _tick () {
        if (this.state.pick) return;
        const now = Date.now();
        if (now - this._start < GRACE_MS) return;      // 막 들어온 사람은 그대로 둠
        if (now - this._last < IDLE_MS) return;        // 아직 작업 중
        if (this._doneForNow()) { clearInterval(this._timer); return; }
        // 따라하기 패널이 열려 있으면(같은 우하단 자리) 다음 기회에 — 사용자가 의도적으로 연 쪽이 우선
        try { if (window.__curoGuideOpen) return; } catch (e) { /* noop */ }
        // 무엇을 권할지: 날짜 기반으로 돌려가며(무작위 X — 매번 같은 게 안 뜨게, 예측 가능하게)
        const d = new Date();
        const idx = (d.getFullYear() + d.getMonth() + d.getDate()) % PICKS.length;
        try { localStorage.setItem(SEEN_DAY, today()); } catch (e) { /* noop */ }
        this.setState({pick: PICKS[idx]});
    }
    _close (ignored) {
        if (ignored) {
            try {
                const n = parseInt(localStorage.getItem(IGNORED) || '0', 10) + 1;
                localStorage.setItem(IGNORED, String(n));
            } catch (e) { /* noop */ }
        } else {
            try { localStorage.setItem(IGNORED, '0'); } catch (e) { /* noop */ } // 한 번 눌렀으면 카운터 리셋
        }
        this.setState({pick: null});
        clearInterval(this._timer);
    }
    _l () {
        const k = hubLang((this.props.intl && this.props.intl.locale) || 'en');
        return L[k] || L.en;
    }
    render () {
        const p = this.state.pick;
        if (!p) return null;
        const t = this._l();
        return (
            <div style={S.card}>
                <div style={S.row}>
                    <span style={S.icon} dangerouslySetInnerHTML={{__html: SVC_ICONS[p.icon] || ''}} />
                    <div style={{minWidth: 0}}>
                        <div style={S.head}>{t.head}</div>
                        <div style={S.desc}>{t[p.t]}</div>
                    </div>
                </div>
                <div style={S.btns}>
                    <a
                        style={Object.assign({}, S.go, {background: p.color})}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => this._close(false)}
                    >{t.go}</a>
                    <button style={S.no} onClick={() => this._close(true)}>{t.no}</button>
                </div>
            </div>
        );
    }
}

const S = {
    card: {position: 'fixed', right: 16, bottom: 16, zIndex: 9050, width: 290, background: '#fff',
        borderRadius: 14, border: '1px solid #e0e3ec', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        padding: '13px 15px 14px', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    row: {display: 'flex', gap: 11, alignItems: 'flex-start'},
    icon: {width: 40, height: 40, flex: 'none', display: 'inline-flex'},
    head: {fontSize: 13, fontWeight: 'bold', color: '#1f2540'},
    desc: {fontSize: 12, color: '#67708a', lineHeight: 1.45, marginTop: 3},
    btns: {display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12},
    go: {border: 'none', borderRadius: 15, color: '#fff', fontSize: 12, padding: '6px 15px',
        cursor: 'pointer', textDecoration: 'none', display: 'inline-block'},
    no: {border: 'none', borderRadius: 15, background: '#eef0f5', color: '#4a5169', fontSize: 12,
        padding: '6px 12px', cursor: 'pointer'}
};

IdleSuggest.propTypes = {
    intl: PropTypes.object,
    vm: PropTypes.object
};

export default injectIntl(IdleSuggest);
